'use client';

/**
 * SceneCanvas — renders a solved scene composition (see scene/sceneSolver):
 * paper-white page, watercolor image assets, hand-drawn containers and
 * connectors, and the phrases as plain SVG text lines (SceneText).
 *
 * Layers (bottom → top): anchor image · object images · shapes + text SVG
 * (hand wobble filter on the shapes) · title HTML.
 *
 * Intros (TT-1150): art plays its generated stages or clip (StagedArt);
 * everything else runs once, at mount, at the delay the reveal schedule
 * (scene/reveal.js) gives it — containers and connectors draw themselves on
 * (pathLength + dash), lines fade and rise, trim and icons fade — since
 * React keeps a node across re-solves by its content-based id. A new
 * composition only schedules what it adds; what is already on the page is
 * never redrawn. `instant` marks the canvas `.scene-instant`, which the
 * stylesheet reads as "no intros" alongside the reduced-motion preference.
 *
 * Continuity (TT-1162 note 1): the last composition stays mounted while the
 * next one is solved — a new board version never passes through an empty
 * canvas, so the art's intro and every finished intro survive it — and an
 * element the new solve keeps but moves glides from its old spot to its new
 * one (`movedSince`, a FLIP driven through the Web Animations API on every
 * layer's nodes, keyed by `data-sid`).
 *
 * Fonts: the host must make `fontFamily` available (a @font-face or a Google
 * Fonts link); measurement waits on document.fonts like the curated path.
 */

import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import SceneIcon, { pct } from './SceneIcon';
import SceneText from './SceneText';
import StagedArt from './StagedArt';
import { ensureFontReady, measureText } from '../lib/tapestryText';
import { exportTapestryPng } from '../lib/exportImage';
import { HandFilter, HAND_FILTER_ID } from '../scene/hand';
import { REVEAL, movedSince, scheduleReveal } from '../scene/reveal';
import { solveScene, specAtSentence, specMaxSentence } from '../scene/sceneSolver';
import { useSceneAssets, useTextBoxArt } from '../scene/sceneAssets';
import { placeArt } from '../board/motifArt';
// The intro rules (scene-*-in) live with the classic renderer's stylesheet;
// a host that renders only the scene must still get them.
import '../styles/tapestryAnimations.css';

/**
 * Solve a spec whenever it (or the playhead) changes, keeping placements
 * stable across solves via the previous rects.
 *
 * Returns `[composition, maxSent]`: the composition is null until assets and
 * fonts are ready; `maxSent` (the spec's last sentence ordinal, 0 without a
 * spec) is known immediately so hosts can size a scrubber before the first
 * solve lands. `artStyles` (style names) draws each symbol's art piece
 * tagged with one of them where the manifest has one, its default elsewhere
 * (scene/sceneAssets.js). `textBoxManifest`: the painted text boxes'
 * (useTextBoxArt).
 */
export function useSceneComposition(spec, { currentSent, fontFamily, manifest, textBoxManifest, artStyles } = {}) {
  const { assets, error: assetError } = useSceneAssets(spec, manifest, artStyles);
  const [fontReadyFor, setFontReadyFor] = useState(null);
  const [solved, setSolved] = useState(null);
  const prevRef = useRef(null);
  const prevSpecId = useRef(null);
  const lastSentRef = useRef(null);
  // Reveal schedule (scene/reveal.js): id → delay, carried across solves of
  // the same scene so a node that stays keeps its (finished) intro.
  const revealRef = useRef(new Map());

  // Every face the spec can use (wall specs carry caps + script faces).
  const families = [fontFamily, spec?.fonts?.caps, spec?.fonts?.script].filter(Boolean).join('|');
  // Ready only for the current face set; a change reads as not ready at once.
  const fontReady = fontReadyFor === families;
  useEffect(() => {
    let cancelled = false;
    Promise.all(families.split('|').map((f) => ensureFontReady(f))).then(() => !cancelled && setFontReadyFor(families));
    return () => {
      cancelled = true;
    };
  }, [families]);

  // The solve is a layout effect (synchronous, before paint) so the first
  // composition doesn't flash empty, while the carry-over refs are only
  // written from the effect, never during render.
  // The last solve stays up while its successor is prepared: a new spec
  // object (every board poll delivers one), a new asset set or face
  // re-solves before the next paint, and until then the page keeps what it
  // has rather than unmounting it (TT-1162 note 1). Only no spec at all
  // clears the page.
  // The painted text boxes a big idea with an intent is drawn in (TT-1330): the
  // solve reserves each piece's reach, so it waits for the manifest too.
  const textBoxArt = useTextBoxArt(textBoxManifest);
  const ready = Boolean(spec && assets && fontReady && textBoxArt);
  // No spec, no page; otherwise the last solve until the next one lands.
  const composition = spec ? solved : null;
  useLayoutEffect(() => {
    if (!ready) return;
    if (prevSpecId.current !== `${spec.id}|${fontFamily}`) {
      prevRef.current = null; // a different scene/font starts fresh
      revealRef.current = new Map();
      prevSpecId.current = `${spec.id}|${fontFamily}`;
    }
    // Scrubbing backward forgets placements (mirrors the seen registry): a
    // forward replay then reproduces the live reserve-then-fill order.
    if (lastSentRef.current != null && currentSent < lastSentRef.current) prevRef.current = null;
    lastSentRef.current = currentSent;
    // One measure per (face, size, text) per solve: the fit ladders lay a
    // block out at every rung and a rung shares most of its words with the
    // last, while measureText forces a layout each call.
    const widths = new Map();
    const measure = (text, fs, family = fontFamily) => {
      const k = `${family}|${fs}|${text}`;
      let w = widths.get(k);
      if (w == null) {
        w = measureText(text, fs, family).width;
        widths.set(k, w);
      }
      return w;
    };
    const comp = solveScene(specAtSentence(spec, currentSent), assets, { measure, prev: prevRef.current, fontFamily, textBoxArt });
    comp.delays = scheduleReveal(comp, revealRef.current);
    // The move diff reads the last composition; a functional update gives
    // it the latest without making it a dependency of the solve.
    setSolved((last) => {
      comp.moves = movedSince(last, comp);
      return comp;
    });
    prevRef.current = comp.rects;
  }, [ready, spec, assets, currentSent, fontFamily, textBoxArt]);

  const maxSent = useMemo(() => (spec ? specMaxSentence(spec) : 0), [spec]);
  // assetError: a failed manifest/image load leaves the composition null
  // forever — hosts must surface it, never render a silent blank page
  // (a missing art file once hid behind exactly that; see TT-1046).
  return [composition, maxSent, assetError || null];
}

function useElementSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      const { width, height } = e.contentRect;
      setSize({ width, height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

/**
 * Resolve the camera window (scene units) for a composition: 'fit' (the
 * whole sheet, default) or an explicit { x, y, w, h } window.
 */
export function resolveCamera(camera, composition) {
  const vb = composition?.viewBox;
  if (!vb) return null;
  return camera && typeof camera === 'object' ? camera : { x: 0, y: 0, w: vb.width, h: vb.height };
}

const CAMERA_EASE_MS = 900;
const EMPTY_DELAYS = new Map();

/**
 * @param onSentClick  (sent, id) => void — makes every text a link to its
 *                     sentence (SceneText); absent, the board is inert
 * @param meetingName, meetingDate  name the export, as on the classic canvas
 * Imperative handle (via ref), the classic canvas's: `{ exportPng() }`
 * downloads the board as a PNG (lib/exportImage) — the wall root is a stack
 * of DOM + SVG layers, and the art's settled image is what the capture
 * sees (TT-1168 note 5). `drawingRect()` is the client rect of the visible
 * window, sized to its aspect, so nothing is letterboxed (lab annotations,
 * TT-1220).
 */
const SceneCanvas = forwardRef(function SceneCanvas({ composition, currentSent = Infinity, showDebug = false, instant = false, imageClient = null, camera = 'fit', style, onSentClick = null, meetingName, meetingDate }, hostRef) {
  const ref = useRef(null);
  useImperativeHandle(
    hostRef,
    () => ({
      exportPng: () => exportTapestryPng(ref.current, { meetingName, dateStr: meetingDate, tag: 'SceneCanvas' }),
      drawingRect: () => ref.current?.getBoundingClientRect() ?? null,
    }),
    [meetingName, meetingDate]
  );
  const { height } = useElementSize(ref);
  const vb = composition?.viewBox;
  // The visible box shows a camera window over the wall. The wall layer is
  // sized in percentages of the window so everything inside (which is laid
  // out in percentages of the wall) rides along; moving the camera is a CSS
  // transition on the layer's box.
  const cam = resolveCamera(camera, composition) || { x: 0, y: 0, w: 1, h: 1 };
  const camAspect = cam.w / cam.h;
  const layer = vb
    ? {
        left: `${(-cam.x / cam.w) * 100}%`,
        top: `${(-cam.y / cam.h) * 100}%`,
        width: `${(vb.width / cam.w) * 100}%`,
        height: `${(vb.height / cam.h) * 100}%`,
      }
    : null;
  // Pixel height of the wall layer (the SVG layers scale by viewBox; the
  // HTML title sizes its font from this).
  const wallH = vb ? (height * vb.height) / cam.h : 0;
  // Per-element intro delays from the composition's reveal schedule.
  const delays = composition?.delays || EMPTY_DELAYS;
  // Glide every node the new composition moved: rendered at its new place,
  // started at its old one. Runs after the commit that moved it. Text
  // lines are SVG (user units); icons and art are HTML boxes in wall
  // percentages, so their delta is scaled to pixels.
  useLayoutEffect(() => {
    const moves = composition?.moves;
    const root = ref.current;
    if (!moves?.size || !root || instant) return;
    const px = wallH / vb.height;
    root.querySelectorAll('[data-sid]').forEach((el) => {
      const move = moves.get(el.dataset.sid);
      if (!move || !el.animate) return;
      const k = el instanceof SVGElement ? 1 : px;
      el.animate([{ transform: `translate(${-move[0] * k}px, ${-move[1] * k}px)` }, { transform: 'none' }], { duration: REVEAL.glide, easing: 'ease-in-out' });
    });
  }, [composition, instant, wallH, vb]);

  return (
    <div
      ref={ref}
      className={instant ? 'scene-canvas scene-instant' : 'scene-canvas'}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: `${vb ? camAspect : composition?.aspect || 16 / 9}`,
        background: composition?.palette?.paper || '#fff',
        overflow: 'hidden',
        ...style,
      }}
    >
      {composition && vb && (
        <div
          className="scene-wall"
          style={{
            position: 'absolute',
            ...layer,
            transition: `left ${CAMERA_EASE_MS}ms ease, top ${CAMERA_EASE_MS}ms ease, width ${CAMERA_EASE_MS}ms ease, height ${CAMERA_EASE_MS}ms ease`,
          }}
        >
          {composition.images.map((im) => (
            <StagedArt
              key={im.id}
              sid={im.id}
              src={im.url}
              reveal={im.reveal}
              opacity={im.opacity ?? 1}
              className={im.id === 'anchor' ? 'scene-anchor' : 'scene-object'}
              style={{ left: pct(im.x, vb.width), top: pct(im.y, vb.height), width: pct(im.w, vb.width), height: pct(im.h, vb.height) }}
            />
          ))}

          {(composition.icons || []).map((ic) => (
            <SceneIcon key={ic.id} icon={ic} viewBox={vb} imageClient={imageClient} delay={delays.get(ic.id) || 0} />
          ))}

          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${vb.width} ${vb.height}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}
          >
            <defs>
              <HandFilter id={HAND_FILTER_ID} />
            </defs>
            {/* Painted text boxes (TT-1330): a piece's tiles, under the inked shapes and outside the wobble filter */}
            <g>
              {composition.shapes.filter((s) => s.kind === 'piece').map((s) => {
                const delay = delays.get(s.id);
                const { tiles } = placeArt(s.piece, s.rect);
                return (
                  <g key={s.id} data-sid={s.id} className="scene-shape scene-shape-in scene-piece-in" style={delay ? { animationDelay: `${delay}ms` } : undefined}>
                    {tiles.map((t, k) => (
                      <svg key={k} x={t.x} y={t.y} width={t.w} height={t.h} viewBox={`${t.sx} ${t.sy} ${t.sw} ${t.sh}`} preserveAspectRatio="none">
                        <image href={s.piece.url} x={0} y={0} width={s.piece.width} height={s.piece.height} preserveAspectRatio="none" />
                      </svg>
                    ))}
                  </g>
                );
              })}
            </g>
            <g filter={`url(#${HAND_FILTER_ID})`} strokeLinecap="round" strokeLinejoin="round">
              {composition.shapes.filter((s) => s.kind !== 'piece').map((s) => {
                const delay = delays.get(s.id);
                return (
                <path
                  key={s.id}
                  data-sid={s.id}
                  d={s.d}
                  stroke={s.stroke}
                  fill={s.fill || 'none'}
                  fillOpacity={s.fillOpacity ?? 1}
                  opacity={s.opacity ?? 1}
                  strokeWidth={s.strokeWidth || 3}
                  strokeDasharray={s.strokeDasharray || undefined}
                  // A unit path length lets one dash rule draw any shape on;
                  // a shape with its own dash pattern (the plumb line) fades
                  // in instead, so the rule never overwrites it.
                  pathLength={s.strokeDasharray ? undefined : 1}
                  className={`scene-shape scene-shape-in scene-${s.strokeDasharray ? 'highlight' : s.kind || 'container'}-in`}
                  style={delay ? { animationDelay: `${delay}ms` } : undefined}
                />
                );
              })}
            </g>
            <SceneText texts={composition.texts} delays={delays} onSentClick={onSentClick} />
            {showDebug && (
              <g fill="none" strokeWidth="2" opacity="0.7">
                {Object.entries(composition.debug.zones).map(([name, poly]) => (
                  <polygon key={`z-${name}`} points={poly.map((p) => p.join(',')).join(' ')} stroke="#2a7ae8" strokeDasharray="8 6" />
                ))}
                {Object.entries(composition.debug.regions).map(([name, poly]) => (
                  <g key={`r-${name}`}>
                    <polygon points={poly.map((p) => p.join(',')).join(' ')} stroke="#e8562a" strokeDasharray="4 4" />
                    <text x={poly[0][0] + 4} y={poly[0][1] + 18} fontSize="16" fill="#e8562a" stroke="none">
                      {name}
                    </text>
                  </g>
                ))}
                {Object.entries(composition.rects).filter(([, r]) => r && typeof r === "object").map(([id, r]) => (
                  <g key={`b-${id}`}>
                    <rect x={r.x} y={r.y} width={r.w} height={r.h} stroke="#10b981" />
                    <text x={r.x + 4} y={r.y - 4} fontSize="14" fill="#10b981" stroke="none">
                      {id}
                      {composition.debug.terrain?.[id] && ` · ${composition.debug.terrain[id].terrain}${composition.debug.terrain[id].dress ? '' : ' (plain)'}`}
                    </text>
                  </g>
                ))}
              </g>
            )}
          </svg>

          {composition.title && (
            <div
              style={{
                position: 'absolute',
                left: pct(composition.title.x, vb.width),
                top: pct(composition.title.y, vb.height),
                fontFamily: `'${composition.fontFamily}', sans-serif`,
                fontSize: `${(composition.title.fontSize / vb.height) * wallH}px`,
                color: composition.title.color,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                textShadow: '2px 2px 0 rgba(0,0,0,0.12)',
                pointerEvents: 'none',
              }}
            >
              {composition.title.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default SceneCanvas;
